import { useMemo, useState, useEffect } from 'preact/hooks'

import { cart } from '@/components/helpers/cart'
import { whenAvailabilityKnown, isPreRelease } from 'libs/athos/helpers/availability'

// `available` is the field normalisers.ts always sets (native Athos `mappings.core.available`,
// or a coerced boolean for synthesised variants). `inventory_quantity` is NOT part of the
// documented Athos variant shape (see normalisers.ts adaptAthosVariants) — it only comes
// through if the AMC feed happens to expose it, so it's a fallback, not the primary check.
export function isVariantOutOfStock(variant) {
  if (typeof variant?.available === 'boolean') return !variant.available
  return variant?.inventory_quantity === 0
}

// `variant.option2` (old Shopify/SearchSpring convention) is never set by the Athos
// normaliser — the option data lives on `variant.options` (raw, e.g. `{ size: { value: 'M' } }`),
// keyed by whatever option name the AMC feed uses. Match case-insensitively rather than
// assuming a literal "size" casing, and fall back to a flattened `variant.size` if present.
export function getVariantSizeLabel(variant) {
  const options = variant?.options
  if (options && typeof options === 'object') {
    const sizeKey = Object.keys(options).find((key) => /size/i.test(key))
    if (sizeKey) {
      const value = options[sizeKey]
      const label = typeof value === 'object' ? value?.value : value
      if (label != null) return label
    }
  }
  return variant?.size ?? variant?.option2 ?? ''
}

// Shared add-to-cart logic for recommendation cards. Extracted so both the Full
// hover overlay (ResultQuickAdd) and the Compact always-visible control
// (CompactQuickAdd) use one source of truth for availability and adding state.
export function useQuickAdd(result) {
  const isAvailable = useMemo(
    () => result?.variants?.some((variant) => !isVariantOutOfStock(variant)),
    [result?.variants]
  )

  const handle = result?.handle

  // Coming Soon products carry real stock (that's the point — inventory is
  // staged ahead of the launch), so `isAvailable` above says nothing about
  // whether they may be bought yet. The status lives in a metafield
  // the Athos feed doesn't expose, hydrated asynchronously by
  // helpers/availability.ts, so this starts `false` and flips once known.
  const [isComingSoon, setIsComingSoon] = useState(() => isPreRelease(handle))

  useEffect(() => {
    let active = true
    setIsComingSoon(isPreRelease(handle))
    whenAvailabilityKnown(handle).then(() => {
      if (active) setIsComingSoon(isPreRelease(handle))
    })
    return () => {
      active = false
    }
  }, [handle])

  // Tracks the variant id currently being added so we can show a spinner and prevent double-adds
  const [addingId, setAddingId] = useState(null)

  const addVariant = async (event, variant) => {
    event.preventDefault()
    event.stopPropagation()

    // Guard against out-of-stock sizes and concurrent adds
    if (addingId || isVariantOutOfStock(variant)) return

    const variantId = typeof variant.id === 'string' ? parseInt(variant.id, 10) : variant.id
    if (!variantId) return

    setAddingId(variant.id)
    try {
      // The authoritative Coming Soon check, not the `isComingSoon` state
      // above: cards render before hydration resolves, so a fast click can
      // arrive while the state still reads `false`. Awaiting the same request
      // the effect uses closes that window at no extra cost — by the time a
      // human clicks it has almost always already settled.
      await whenAvailabilityKnown(handle)
      if (isPreRelease(handle)) {
        setIsComingSoon(true)
        return
      }

      const data = await cart.addLineItems([{ id: variantId, quantity: 1 }])
      if (data?.error) throw new Error(data.error)
    } catch (error) {
      console.error('Error adding item to cart:', error)
    } finally {
      setAddingId(null)
    }
  }

  return { isAvailable, isComingSoon, addingId, addVariant }
}
